import React from "react";
import clsx from "clsx";
import styles from "./ComponentCard.module.css";
import type { SharedCardRenderProps } from "./ComponentCard.types.ts";
import StockStatusDisplay from "./StockStatusDisplay.tsx";
import ValidationBadges from "./ValidationBadges.tsx";
import { ExclamationMarkTriangle } from "../common/icons";
import { useStrings } from "../../context/ConfiguratorStringsContext.ts";

const productImageUrl = (id: string, explicit?: string): string =>
	explicit ?? `/img/p/200/${id}.jpg`;

export interface MultipleSelectionCardContentProps extends SharedCardRenderProps {
	multipleSelectionItems?: Array<{
		id: string;
		title?: string;
		imageUrl?: string;
		stockStatus?: { iconClass?: string; description?: string };
	}>;
}

const MultipleSelectionCardContent: React.FC<MultipleSelectionCardContentProps> = ({
	kind,
	isCustomized,
	isReplaced,
	validationStatus,
	validationErrors,
	children,
	productGroupId,
	onSelectClick,
	onReadMoreClick,
	multipleSelectionItems,
}) => {
	const strings = useStrings();
	const isEmpty = !multipleSelectionItems || multipleSelectionItems.length === 0;
	const isSingleItem = multipleSelectionItems?.length === 1;

	if (isEmpty) {
		return (
			<>
				<div className={styles.imageContainer}>
					<div className={clsx(styles.thumbnail, styles.emptyThumbnail)}>
						<span className={styles.emptyIcon} aria-hidden="true">+</span>
					</div>
				</div>
				<div className={styles.content}>
					<div className={styles.header}>
						<div className={styles.titleSection}>
							<div className={styles.kind}>{kind}</div>
							<h3 className={styles.title}>
								{strings.product.configBundles?.notSelected?.webText1}
							</h3>
						</div>
					</div>
					<div className={styles.footer}>
						<div className={styles.footerSpacer} />
						<button
							className={styles.selectButton}
							onClick={onSelectClick}
							aria-label={`${strings.product.configBundles.notSelected.notChosen}: ${kind}`}
							data-testid={`component-card-group-${productGroupId}-edit-button`}
						>
							{strings.product.configBundles.notSelected.notChosen}
						</button>
					</div>
				</div>
			</>
		);
	}

	if (isSingleItem) {
		const item = (multipleSelectionItems ?? [])[0];
		return (
			<>
				<div className={styles.imageContainer}>
					<img
						src={productImageUrl(item.id, item.imageUrl)}
						alt={item.title ?? ""}
						className={styles.thumbnail}
					/>
				</div>
				<div className={styles.content}>
					<div className={styles.header}>
						<div className={styles.titleSection}>
							<div className={styles.kind}>
								{kind}
								{isCustomized && (
									<span className={styles.customizedBadge}>
										{" "}
										• {strings.product.configBundles.configured}
									</span>
								)}
							</div>
							<h3 className={styles.title}>{item.title}</h3>
						</div>
					</div>
					{children}
					<div>
						{item.stockStatus && (
							<div className={styles.leftFooterSection}>
								<StockStatusDisplay stockStatus={item.stockStatus} />
							</div>
						)}
					</div>
					<div className={styles.footer}>
						{isReplaced && (
							<span className={clsx(styles.validationBadge, styles.warning)}>
								<ExclamationMarkTriangle aria-hidden="true" />
								{strings.product.configBundles.componentReplaced}
							</span>
						)}
						{validationStatus && (
							<ValidationBadges
								validationStatus={validationStatus}
								validationErrors={validationErrors}
							/>
						)}
						<button
							className={styles.linkButton}
							onClick={onReadMoreClick}
							aria-label={`${strings.product.configBundles.readMore}: ${item.title ?? kind}`}
							data-testid={`component-card-group-${productGroupId}-read-more-button`}
						>
							{strings.product.configBundles.readMore}
						</button>
						<button
							className={styles.selectButton}
							onClick={onSelectClick}
							aria-label={`${strings.product.configBundles.edit}: ${item.title ?? kind}`}
							data-testid={`component-card-group-${productGroupId}-edit-button`}
						>
							{strings.product.configBundles.edit}
						</button>
					</div>
				</div>
			</>
		);
	}

	return (
		<div className={styles.content}>
			<ul className={styles.multipleItemsList}>
				{(multipleSelectionItems ?? []).map((item, index) => (
					<li key={item.id} className={styles.multipleItemRow}>
						<div className={styles.multipleItemImage}>
							<img
								src={productImageUrl(item.id, item.imageUrl)}
								alt={item.title ?? ""}
								className={styles.thumbnail}
							/>
						</div>
						<div className={styles.multipleItemDetails}>
							{index === 0 && (
								<div className={styles.kind}>
									{kind}
									{isCustomized && (
										<span className={styles.customizedBadge}>
											{" "}
											• {strings.product.configBundles.configured}
										</span>
									)}
								</div>
							)}
							<span className={styles.title}>{item.title}</span>
							{item.stockStatus && (
								<StockStatusDisplay stockStatus={item.stockStatus} />
							)}
						</div>
					</li>
				))}
			</ul>
			{children}
			<div className={styles.footer}>
				{isReplaced && (
					<span className={clsx(styles.validationBadge, styles.warning)}>
						<ExclamationMarkTriangle aria-hidden="true" />
						{strings.product.configBundles.componentReplaced}
					</span>
				)}
				{validationStatus && (
					<ValidationBadges
						validationStatus={validationStatus}
						validationErrors={validationErrors}
					/>
				)}
				<button
					className={styles.selectButton}
					onClick={onSelectClick}
					aria-label={`${strings.product.configBundles.edit}: ${kind}`}
					data-testid={`component-card-group-${productGroupId}-edit-button`}
				>
					{strings.product.configBundles.edit}
				</button>
			</div>
		</div>
	);
};

export default MultipleSelectionCardContent;
